import React, { useState, useEffect, memo, useImperativeHandle } from "react";
import { Text, View, TouchableOpacity } from "react-native";

import moment from "moment";
import { findIndex } from "lodash";

import cardStyles from "./cardStyles";

import Utils, {
  copyDeep,
  getStorageKey,
  hasItems,
  timeslotsEqual,
} from "../../../../helpers/utils";

import TimeSlotsWrapper from "../../journal/timeslots/TimeSlotsWrapper";

import LoadingIndicator from "../../common/LoadingIndicator";
import JournalHelper from "../../journal/JournalHelper";
import Icons from "../../common/icons/index";
import Toggle from "../../common/Toggle";
import SleepCard from "../sleep/SleepCard";
import { AppStateWatcher } from "../../common/AppStateWatcher";

const DATE_FORMAT = "YYYY-MM-DD";
const THRESHOLD_REFRESH_MS = 5 * 60 * 1000;

const HomeCard = React.forwardRef(
  (
    {
      controller,
      navigation,
      toggleJournalDialog = () => {},
      toggleTimePickerDialog = () => {},
      currentTimePickerSlot,
      expandedSlots,
      goToEditPage = () => {},
      openMenuOptions = () => {},
    },
    ref,
  ) => {
    const [minutesInterval, setMinutesInterval] = useState();
    const [tzOverride, setTzOverride] = useState();
    const [selectedTimeslots, setSelectedTimeslots] = useState();
    const [timeslotsUpdateCounter, setTimeslotsUpdateCounter] = useState(0);
    const [lastUpdateTime, setLastUpdateTime] = useState(0);
    const [allTimeslots, setAllTimeslots] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(0);
    const [isToday, setIsToday] = useState(false);
    const [isTrueNowActive, setIsTrueNowActive] = useState(null);

    // Avatar click from header
    useImperativeHandle(ref, () => ({
      refresh() {
        fetchTimeslots(false);
      },

      confirmTimeslot,
    }));

    const updateSelectedDate = () => {
      Utils.getStorageItem("home_card_show_now").then((showNow) => {
        const newDate = showNow
          ? Utils.nowUserTz(controller).format("YYYY-MM-DD HH:mm:ss")
          : null;

        setSelectedDate(newDate);
        if (showNow) setIsTrueNowActive(showNow.isTrueNowActive);
      });
    };

    useEffect(() => {
      // End time confirmed
      if (currentTimePickerSlot) {
        toggleCardTimePickerDialog(currentTimePickerSlot);
      }
    }, [currentTimePickerSlot]);

    useEffect(() => {
      setMinutesInterval(controller.state?.settings?.interval_duration);
      updateSelectedDate();
    }, []);

    useEffect(() => {
      if (tzOverride !== controller.state?.settings?.tz_override) {
        setTzOverride(controller.state?.settings?.tz_override);
        setMinutesInterval(controller.state?.settings?.interval_duration);
        updateSelectedDate();
        setIsToday(
          moment(selectedDate).isSame(Utils.nowUserTz(controller), "day"),
        );
        fetchTimeslots();
      }
    }, [controller.state?.settings]);

    useEffect(() => {
      updateFromState();
    }, [controller.state?.calendarItems, controller.state?.timeslots_local_update_time]);

    useEffect(() => {
      if (selectedDate == 0) return;

      setIsToday(
        moment(selectedDate).isSame(Utils.nowUserTz(controller), "day"),
      );
      updateFromState();
    }, [selectedDate, controller.state?.timeslots, isTrueNowActive]);

    useEffect(() => {
      const data = isTrueNowActive != null ? { isTrueNowActive } : null;

      Utils.setStorageItem(
        getStorageKey(controller, "home_card_show_now"),
        data,
      );
    }, [isTrueNowActive]);

    const updateFromState = () => {
      if (controller.state.timeslots != null) {
        return initTimeslots(controller.state.timeslots, selectedDate); //instant init
      } else {
        return false;
      }
    };

    // ------------------------------------- API -------------------------------------
    const fetchTimeslots = (showSpinner = true) => {
      if (controller.state.currentUser == null) return;

      if (showSpinner) setIsLoading(true);

      const oldTimeslots = controller.state.timeslots
        ? JSON.parse(JSON.stringify(controller.state.timeslots))
        : null;

      // Resync with server
      Utils.getStorageItem(
        Utils.getStorageKey(controller, "home_card_show_now"),
      ).then((showNow) => {
        const newDate = showNow
          ? Utils.nowUserTz(controller).format("YYYY-MM-DD HH:mm:ss")
          : null;
        setSelectedDate(newDate);
        if (showNow) setIsTrueNowActive(showNow?.isTrueNowActive);

        const params = newDate ? { timeslotsDate: newDate } : null;
        console.log("Fetch timeslots");
        controller.getApiData("timeslots", params, (timeslots) => {
          if (!timeslotsEqual(oldTimeslots, timeslots, newDate)) {
            console.log("Retrieved new timeslots");
            initTimeslots(timeslots, newDate);
          } else {
            var now = +new Date();
            if (now - lastUpdateTime > THRESHOLD_REFRESH_MS) {
              setLastUpdateTime(now);
              setTimeslotsUpdateCounter(timeslotsUpdateCounter + 1);
            } else {
              console.log("Timeslots identical, skip update");
            }
          }
          setIsLoading(false);
        });
      });
    };

    const initTimeslots = (timeslots = [], newDate = null) => {
      const ts = timeslots
        .filter((t) => t)
        .map((t, index) => {
          const startDate = moment(t.time);
          const endDate = moment(t.time).add(t.duration, "minutes");

          return {
            ...t,
            // To determine prev timeslots
            position: index,
            startDate: startDate.format(DATE_FORMAT),
            endDate: endDate.format(DATE_FORMAT + " HH:mm:ss"),
            startTime: startDate.format("h:mm A"),
            oldStartTime: startDate.clone().format("h:mm A"),
            endTime: endDate.format("h:mm A"),
            oldEndTime: endDate.clone().format("h:mm A"),
          };
        });

      // Needed on confirm to get affected timeslot ids
      setAllTimeslots(ts);

      return getLastEditedTimeslots(ts, newDate);
    };

    const hasPredictions = (t) => {
      return t && hasItems(t.predicted_activities);
    };

    const canShiftDown = (ts, index) => {
      return (
        hasPredictions(ts[index - 2]) &&
        !hasPredictions(ts[index - 1]) &&
        !hasPredictions(ts[index])
      );
    };

    const findFirstUnconfirmed = (ts, startDate, endDate) => {
      const foundIndex = findIndex(ts, (t, index) => {
        return (
          (!isTrueNowActive && canShiftDown(ts, index)) || // True-Now logic (as per specs)
          (Utils.hasItems(t.predicted_activities) &&
            (endDate == null || t.startDate <= endDate) &&
            (startDate == null || t.endDate >= startDate))
        );
      });
      return foundIndex;
    };

    const expandTimeslot = (t) => {
      if (t && t.id) {
        const ids = t.id.split(",");
        const slots = expandedSlots.slice(0);
        for (var i in ids) {
          if (!slots.includes(ids[i])) slots.push(ids[i]);
        }
        expandedSlots.length = 0;
        expandedSlots.push(...slots);

        controller.tracking.track("SlotExpand");
        updateFromState();
      }
    };

    const getLastEditedTimeslots = (timeslots, newDate) => {
      let prevTimeslot = null;
      const mergedTimeslots = [];

      JournalHelper.biasPredictions(controller, timeslots);
      JSON.parse(JSON.stringify(timeslots)).forEach((t, index) => {
        if (
          !JournalHelper.mergeTimeslot(
            prevTimeslot,
            t,
            false,
            0,
            expandedSlots,
            false,
          )
        ) {
          prevTimeslot = copyDeep(t);
          mergedTimeslots.push(prevTimeslot);
        }
      });

      const ts = mergedTimeslots;
      let foundIndex = findFirstUnconfirmed(ts, newDate, newDate);
      if (foundIndex === -1)
        foundIndex = findFirstUnconfirmed(ts, newDate, null);
      if (foundIndex === -1) foundIndex = findFirstUnconfirmed(ts, null, null);
      setIsLoading(false);

      const selected = ts.slice(
        Math.max(0, foundIndex - 2),
        Math.min(ts.length, foundIndex + 1 - Math.min(0, foundIndex - 2)),
      );

      setSelectedTimeslots([...selected]);
      setLastUpdateTime(+new Date());
      setTimeslotsUpdateCounter(timeslotsUpdateCounter + 1);
      return selected.length > 0;
    };

    const toggleCardTimePickerDialog = (timeslot) => {
      // Time to close the time picker dialog
      if (timeslot.hasUpdatedEndTime) {
        // Reset popup state
        delete timeslot.hasUpdatedEndTime;

        // Show user updated end-time
        const new_ts = selectedTimeslots.map((t) => {
          return t.id === timeslot.id ? { ...timeslot } : t;
        });

        // Close timepicker
        toggleTimePickerDialog(null);
        setSelectedTimeslots([...new_ts]);
        setLastUpdateTime(+new Date());
        setTimeslotsUpdateCounter(timeslotsUpdateCounter + 1);

        // Autoconfirm timeslot for prev confirmed activities
        if (timeslot.activity) {
          confirmTimeslot({ timeslot });
        }
      }
    };

    const confirmTimeslot = (ts) => {
      setIsLoading(true);

      // if NOW tab selected active disable TrueNow
      if (selectedDate) setIsTrueNowActive(false);

      controller.tracking.track("Confirm HomeCard");
      JournalHelper.saveTimeslotToDB(
        controller,
        allTimeslots,
        {
          ...ts,
          reload: true,
        },
        onConfirmComplete,
      );
    };

    const onConfirmComplete = () => {
      initTimeslots(controller.state.timeslots, selectedDate); //instant update
    };

    const toggleSearchInterval = (option) => {
      let newDate = null;

      // Pressing NOW twice: toggle between TrueNow and TrackNow
      if (option == "Now") {
        newDate = Utils.nowUserTz(controller).format("YYYY-MM-DD HH:mm:ss");
        setIsTrueNowActive(!isTrueNowActive);
      } else {
        setIsTrueNowActive(null);
      }

      setSelectedDate(newDate);
    };

    const goToTrackHomePage = () => {
      controller.changePage("feed", {
        selectedPage: "rn:journal",
        args: {},
      });
    };

    const onRenderComplete = () => {
      setIsLoading(false);
    };

    const lastTimeslot = selectedTimeslots
      ? selectedTimeslots[selectedTimeslots.length - 1]
      : {};
    const selecteDateName = moment(lastTimeslot?.time).format("ddd, MMM DD");

    const journalContext = {
          controller: controller,
          onTimeslotSelected: goToEditPage,
          confirmTimeslot: confirmTimeslot,
          openJournalDialog: toggleJournalDialog,
          toggleTimePicker: toggleTimePickerDialog,
          expandTimeslot: expandTimeslot,
          isAutoReview: true,
          onRenderComplete: onRenderComplete,
          openMenuOptions,
        };

    return (
      <>
        <View style={cardStyles.container}>
          <LoadingIndicator isLoading={isLoading} />

          <View style={cardStyles.card}>
            {/* Header */}
            <View style={[cardStyles.header, { paddingHorizontal: 0 }]}>
              <Icons.HourGlassIcon onPress={goToTrackHomePage} />
              <TouchableOpacity
                style={[cardStyles.headerInfo, { marginLeft: 6 }]}
                onPress={goToTrackHomePage}
              >
                <Text style={[cardStyles.title]}>{selecteDateName}</Text>
                {isToday ? (
                  <Text style={[cardStyles.date]} onPress={goToTrackHomePage}>
                    Today
                  </Text>
                ) : null}
              </TouchableOpacity>
              <Toggle
                style={{ height: 24, borderRadius: 6 }}
                selected={!selectedDate ? "Before" : "Now"}
                options={["Before", "Now"]}
                onToggle={toggleSearchInterval}
              />
            </View>

            <View style={{ maxWidth: "100%", marginTop: 8, marginLeft: -10 }}>
              <TimeSlotsWrapper
                key={"home_timeslots"}
                controller={controller}
                journalContext={journalContext}
                navigation={navigation}
                source={"home"}
                timeslots={selectedTimeslots}
                timeslotsUpdateCounter={timeslotsUpdateCounter}
                isHomeCard
                showIcons={true}
                containerStyle={{ paddingBottom: 0, minHeight: 170 }}
              />
            </View>
          </View>
        </View>

        <SleepCard
          controller={controller}
          timeslot={lastTimeslot}
          minutesInterval={minutesInterval}
          onConfirmComplete={onConfirmComplete}
          toggleTimePickerDialog={toggleTimePickerDialog}
        />
        <AppStateWatcher onAppBecomesActive={() => fetchTimeslots()} />
      </>
    );
  },
);

export default memo(HomeCard);
